<template>
	<BasicDialog @close="closeClicked" :append-bottom="true">
		<div>

			<OfferBasicInformation :offer="selectedOffer" />

			<v-divider class="my-5 my-sm-6"></v-divider>

			<div class="offer-stats" v-if="!isMobileDevice">
				<OfferDetailRatingTile :likes="calculateRating.totalRating" />
				<ProviderTile :offer="selectedOffer" />
			</div>

			<v-row no-gutters class="mt-6">
				<OfferInstructions :instructions="selectedOffer.instructions"></OfferInstructions>
			</v-row>

			<OfferMultiSteps />

			<div class="d-flex flex-column align-center mt-10 mb-2">
				<slot>
					<div>{{ selectedOffer.call_to_action.disclaimer }}</div>
				</slot>
				<div class="d-flex justify-center mt-2">
					<button class="button--link" @click="reportOfferClicked">
						<v-icon class="button__icon"><WarningSign /></v-icon>
						<span class="button__text">Report Offer</span>
					</button>
				</div>
			</div>
		</div>

		<div class="offer-controls mt-4">
			<div class="offer-controls__feedback-group mb-4">
				<button class="offer-control__feedback-button rounded-circle"
					:class="{ 'offer-control__feedback-button--selected': selectedIcon === 'like' }" @click="likeOffer">
					<LikedIcon></LikedIcon>
				</button>
				<button class="offer-control__feedback-button rounded-circle"
					:class="{ 'offer-control__feedback-button--selected': selectedIcon === 'dislike' }"
					@click="dislikeOffer">
					<DislikedIcon></DislikedIcon>
				</button>
			</div>
			<v-btn rounded="pill" class="button--primary px-16 py-6 d-flex" @click="startOffer">Start Offer</v-btn>
		</div>

	</BasicDialog>
</template>



<script setup lang="ts">

import { isMobile } from "@/utils/utils";
import { OfferStore } from "@/store/offerStore";
import { Offer } from "@/interfaces/Offers/Offer";
import { OfferService } from "@/api/services/offerService";

import ProviderTile from "./Provider-Tile.vue";
import OfferDetailRatingTile from "./Rating-Tile.vue";
import OfferMultiSteps from "./Offer-Multi-Steps.vue";
import OfferInstructions from "./Offer-Instructions.vue";
import { computed, inject, provide, ref, watch, onMounted } from "vue";
import BasicDialog from "@/components/Common/Basic-Dialog.vue";
import OfferBasicInformation from "./Offer-Basic-Information.vue";
import { useRouter, useRoute } from "vue-router";

import LikedIcon from "@/icons/Liked.vue";
import DislikedIcon from "@/icons/DislikedIcon.vue";
import WarningSign from "@/icons/WarningSign.vue";

const isMobileDevice = isMobile();
const router = useRouter();
const route = useRoute();

const props = defineProps({
	offer: {
		type: Object as () => Offer,
		default: null,
	}
});

const selectedIcon = ref('');

const emit = defineEmits(["close", "report-offer"]);

const offerDetailsDialogOpen = inject("offerDetailsDialogOpen");

const openDialog = ref(offerDetailsDialogOpen);

provide("openDialog", openDialog);

const selectedOffer = computed(() => props.offer);

// Update URL when dialog is opened
watch(openDialog, (newValue) => {
	if (newValue && selectedOffer.value?.id) {
		// Update URL with offer ID without reloading the page
		// Check which page we're on to determine how to update the URL
		if (route.path === '/offers' || route.name === 'OfferDetails') {
			// For the Offers page, use route parameters
			// Use history.replaceState to update URL without scrolling
			const newUrl = `/offers/${selectedOffer.value.id}`;
			window.history.replaceState(null, '', newUrl);
		} else {
			// For other pages (like Earn), use query parameters
			// Use history.replaceState to update URL without scrolling
			const searchParams = new URLSearchParams(window.location.search);
			searchParams.set('offerId', selectedOffer.value.id.toString());
			const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
			window.history.replaceState(null, '', newUrl);
		}
	} else if (!newValue) {
		// Remove offer ID from URL when dialog is closed
		if (route.path === '/offers' || route.name === 'OfferDetails') {
			// Use history.replaceState to update URL without scrolling
			window.history.replaceState(null, '', '/offers');
		} else {
			// For other pages, just remove the query parameter
			const searchParams = new URLSearchParams(window.location.search);
			searchParams.delete('offerId');
			const newUrl = searchParams.toString() 
				? `${window.location.pathname}?${searchParams.toString()}` 
				: window.location.pathname;
			window.history.replaceState(null, '', newUrl);
		}
	}
});

const closeClicked = () => {
	emit("close");
	// The URL will be updated by the watcher above
};

const offerStore = OfferStore()
const startOffer = () => offerStore.startOffer();

const likeOffer = async () => {
	if (selectedIcon.value === 'like') return;
	if (selectedIcon.value === 'dislike') {
		selectedOffer.value.rating_info.dislikes -= 1;
	}
	selectedIcon.value = 'like';
	selectedOffer.value.rating_info.likes += 1;
	selectedOffer.value.rating_info.my_rating = 'like';

	// Send the updated likes count to your API
	await OfferService.rateOffer(selectedOffer.value.id, 'like');
};

const dislikeOffer = async () => {
	if (selectedIcon.value === 'dislike') return;
	if (selectedIcon.value === 'like') {
		selectedOffer.value.rating_info.likes -= 1;
	}
	selectedIcon.value = 'dislike';
	selectedOffer.value.rating_info.dislikes += 1;
	selectedOffer.value.rating_info.my_rating = 'dislike';

	// Send the updated dislikes count to your API
	await OfferService.rateOffer(selectedOffer.value.id, 'dislike');
};

watch(selectedOffer, () => {
	selectedIcon.value = '';
});

const reportOfferClicked = () => {
	emit("report-offer");
};

const calculateRating = computed(() => {
	const likes = selectedOffer.value.rating_info?.likes;
	const dislikes = selectedOffer.value.rating_info?.dislikes;
	const totalcount = likes + dislikes;
	const totalRating = totalcount === 0 ? 0 : Math.round((likes / totalcount) * 100);
	return { likes, dislikes, totalRating };
});

</script>



<style scoped lang="scss">
.offer-stats {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}

.offer-controls {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;

	&__feedback-group {
		display: flex;
		gap: 10px;
	}
}

</style>